import { MRT_ColumnDef } from 'material-react-table';
import { IOrdersRowData } from '../../../interfaces/orders';
import { CopyComponent } from '../../../components/CopyComponent';
import { Link } from 'react-router-dom';
import { numberFormatter } from '../../../utils/utilities';
import React from 'react';
import { ColumnSizing } from '../tables-context';
import { TableCell } from '../../../components/table/TableCell';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';
import { DateComponent } from '../../../components/DateComponent';
import { StatusBadge } from '../../../components/status/StatusBadge';

export enum OrdersTableAccessorKeys {
	id = 'id',
	referenceNumber = 'referenceNumber',
	createdOn = 'createdOn',
	orderPlacedDate = 'orderPlacedDate',
	merchantName = 'merchantName',
	payerName = 'payerName',
	isMIT = 'isMIT',
	descriptor = 'descriptor',
	statusName = 'statusName',
	amount = 'amount',
	refundsAmount = 'refundsAmount',
	siteId = 'siteId',
}

export const defaultOrdersColumnSizing: ColumnSizing = {
	[OrdersTableAccessorKeys.id]: 150,
	[OrdersTableAccessorKeys.referenceNumber]: 150,
	[OrdersTableAccessorKeys.createdOn]: 120,
	[OrdersTableAccessorKeys.orderPlacedDate]: 120,
	[OrdersTableAccessorKeys.merchantName]: 150,
	[OrdersTableAccessorKeys.payerName]: 150,
	[OrdersTableAccessorKeys.isMIT]: 80,
	[OrdersTableAccessorKeys.descriptor]: 150,
	[OrdersTableAccessorKeys.statusName]: 100,
	[OrdersTableAccessorKeys.amount]: 75,
	[OrdersTableAccessorKeys.refundsAmount]: 75,
	[OrdersTableAccessorKeys.siteId]: 100,
};


export const defaultOrdersColumns: MRT_ColumnDef<IOrdersRowData>[] = [
	{
		accessorKey: OrdersTableAccessorKeys.id,
		header: 'Order id',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Order id</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => {
			const path = `/orders/${renderedCellValue}`;
			const isGhostMode = row?.original?.isGhostMode;
			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<CopyComponent value={renderedCellValue} title="Order Id"/>
						<Link
							to={path}
							state={{ isGhostMode }}
							className="text-black fs-7 ms-2 fc-text-overflow"
						>
							<div className="fs-7 ms-2 fc-text-overflow">
								{renderedCellValue}
							</div>
						</Link>
				</TableCell>
		);
		}
	},
	{
		accessorKey: OrdersTableAccessorKeys.referenceNumber,
		header: 'External order',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">External order</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<CopyComponent value={renderedCellValue} title="External Order"/>
				<div className="fs-7 ms-2 fc-text-overflow">{renderedCellValue ? renderedCellValue : '-'}</div>
			</TableCell>
		)
	},
	{
		accessorKey: OrdersTableAccessorKeys.siteId,
		header: 'Site id',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Site id</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				{renderedCellValue && <CopyComponent value={renderedCellValue} title="Site Id"/>}
				<div className="fs-7 ms-2 fc-text-overflow">{renderedCellValue ? renderedCellValue : '-'}</div>
			</TableCell>
		)
	},
	{
		accessorKey: OrdersTableAccessorKeys.createdOn,
		header: 'Created date',
		Header: <span className="text-muted fw-bolder main-font-family">Created date</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				year === 1
					? <div className="fw-bolder">-</div>
					: (
						<TableCell
							additionalClass="main-font-family text-black"
							density={table?.options?.state?.density}
							isTextOverflow={false}
						>
							{/*{getTableDate(renderedCellValue, true)}*/}
							<DateComponent
								value={renderedCellValue}
								placement="top"
								isTooltip
								isTime
							/>
						</TableCell>
					)
			);
		}
	},
	{
		accessorKey: OrdersTableAccessorKeys.orderPlacedDate,
		header: 'Placed date',
		Header: <span className="text-muted fw-bolder main-font-family">Placed date</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				year === 1
					? <div className="fw-bolder">-</div>
					: (
						<TableCell
							additionalClass="main-font-family text-black"
							density={table?.options?.state?.density}
							isTextOverflow={false}
						>
							{/*{getTableDate(renderedCellValue, true)}*/}
							<DateComponent
								value={renderedCellValue}
								placement="top"
								isTooltip
								isTime
							/>
						</TableCell>
					)
			);
		}
	},
	{
		accessorKey: OrdersTableAccessorKeys.merchantName,
		header: 'Merchant name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Merchant name</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<RelatedEntityNameLink name={renderedCellValue} id={row?.original?.merchantId} type={RELATED_ENTITY_TYPE.MERCHANT} />
			</ TableCell>
	)
	},
	{
		accessorKey: OrdersTableAccessorKeys.payerName,
		header: 'Customer',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Customer</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fc-text-overflow">
					{renderedCellValue}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: OrdersTableAccessorKeys.isMIT,
		header: 'MIT/CIT',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">MIT / CIT</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? 'MIT' : 'CIT'}
			</TableCell>
		)
	},
	{
		accessorKey: OrdersTableAccessorKeys.descriptor,
		header: 'Descriptor',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Descriptor</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue ? renderedCellValue : '-'}
			</TableCell>
		)
	},
	{
		accessorKey: OrdersTableAccessorKeys.statusName,
		header: 'Status',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Status</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			return (
				<TableCell
					additionalClass="fs-7"
					density={table?.options?.state?.density}
					isTextOverflow={false}
				>
					<StatusBadge status={renderedCellValue} />
				</TableCell>
			);
		}
	},
	{
		accessorKey: OrdersTableAccessorKeys.amount,
		header: 'Amount',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Amount</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex flex-column align-items-start main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<span className="text-dark fw-bolder d-block fs-8">
					{row?.original?.currencySymbol}{numberFormatter(renderedCellValue)}
				</span>
				<span className="fw-bold text-muted d-block fs-9">
					{row?.original?.currency}
				</span>
			</TableCell>
		)
	},
	{
		accessorKey: OrdersTableAccessorKeys.refundsAmount,
		header: 'Refund amount',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Refund amount</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex flex-column align-items-start main-font-family text-black fs-6"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{renderedCellValue
					? (
						<div className="d-flex flex-column align-items-start main-font-family">
							<span className="text-danger fw-bolder d-block fs-8">
								{row?.original?.currencySymbol}{numberFormatter(renderedCellValue)}
							</span>
							<span className="text-muted d-block fs-9">
								{row?.original?.currency}
							</span>
						</div>
					): <div>-</div>
				}
			</TableCell>
		)
	},
// {
// 	accessorKey: 'adjustment',
// 	header: 'Adjustment',
// 	enableSorting: false,
// 	Header: <span className="text-muted fw-bolder main-font-family">Adjustment</span>,
// 	Cell: ({ renderedCellValue, row }: any): JSX.Element => (
// 		<div className="d-flex flex-column align-items-start main-font-family">
// 				{renderedCellValue}
// 		</div>
// 	),
// 	size: 110
// }
];
